import { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  FileText,
  Upload,
  Trash2,
  Eye,
  Plus,
  AlertCircle,
  CheckCircle,
  X,
  Download,
  ExternalLink,
} from "react-feather";
import { SectionDivider } from "../../BaseComponents/ProfileSection";
import BaseSelect from "../../BaseComponents/BaseSelect";
import {
  STAFF_DOCUMENT_TYPES,
  STAFF_DOCUMENT_CATEGORIES,
  DOCUMENT_SECTION,
  DOC_TYPE_COLORS,
  MANDATORY_DOCUMENT_TYPES,
  MANDATORY_DOC_VALUES,
} from "../../Constant/HR_Staff/hrEnums";
import { BaseStaffURL } from "../../Api/Service";

/* ── helpers ── */
const getDocTypeLabel = (v) => STAFF_DOCUMENT_TYPES.find((t) => t.value === v)?.label || v;
const getDocTypeCategory = (v) =>
  STAFF_DOCUMENT_TYPES.find((t) => t.value === v)?.category || "Other";
const getCategoryColor = (c) => DOC_TYPE_COLORS[c] || "#64748b";

/** Derive preview type from filename extension */
const getPreviewType = (fileName) => {
  if (!fileName) return "unknown";
  const ext = fileName.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "office";
  return "unknown";
};

/* ── Document Preview Modal ── */
const DocPreviewModal = ({ file, label, onClose }) => {
  if (!file) return null;
  const url = BaseStaffURL ? BaseStaffURL + file : "#";
  const type = getPreviewType(file);
  const ext = file.split(".").pop().toUpperCase();

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${label}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "fadeIn 0.15s ease",
      }}
    >
      <div
        className="animate-scale-in"
        style={{
          width: "100%",
          maxWidth: 860,
          maxHeight: "90vh",
          background: "var(--dropdown-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 20,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
        }}
      >
        {/* Modal header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "1px solid var(--glass-border)",
            background: "rgba(99,102,241,0.04)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileText size={16} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "var(--text-primary)" }}>
                {label}
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 1 }}>
                {ext} document
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Open in new tab */}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in new tab"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 12px",
                borderRadius: 8,
                background: "rgba(99,102,241,0.08)",
                border: "1px solid rgba(99,102,241,0.2)",
                color: "var(--brand-indigo)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                textDecoration: "none",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(99,102,241,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(99,102,241,0.08)";
              }}
            >
              <ExternalLink size={13} strokeWidth={2} />
              Open
            </a>

            {/* Download */}
            <a
              href={url}
              download
              aria-label="Download file"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 12px",
                borderRadius: 8,
                background: "rgba(16,185,129,0.08)",
                border: "1px solid rgba(16,185,129,0.2)",
                color: "#10b981",
                fontSize: "0.8125rem",
                fontWeight: 600,
                textDecoration: "none",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(16,185,129,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(16,185,129,0.08)";
              }}
            >
              <Download size={13} strokeWidth={2} />
              Download
            </a>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(244,63,94,0.1)";
                e.currentTarget.style.color = "#f43f5e";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--icon-btn-bg)";
                e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              <X size={15} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Preview body */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: type === "image" ? "rgba(0,0,0,0.3)" : "var(--page-bg)",
            padding: type === "image" ? 20 : 0,
          }}
        >
          {type === "image" && (
            <img
              src={url}
              alt={label}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                objectFit: "contain",
              }}
            />
          )}

          {type === "pdf" && (
            <iframe
              src={url}
              title={label}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          )}

          {type === "office" && (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`}
              title={label}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          )}

          {type === "unknown" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
                padding: "60px 40px",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: "rgba(99,102,241,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={32} strokeWidth={1.5} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "var(--text-primary)",
                  }}
                >
                  Preview not available
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "0.875rem", color: "var(--text-muted)" }}>
                  Use the Open or Download button above to view this file
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const useDocTypeOptions = () =>
  useMemo(
    () =>
      STAFF_DOCUMENT_CATEGORIES.map((cat) => ({
        label: cat.category,
        options: cat.types
          .map((tv) => STAFF_DOCUMENT_TYPES.find((t) => t.value === tv))
          .filter(Boolean)
          .map((t) => ({ value: t.value, label: t.label, category: t.category })),
      })),
    [],
  );

/* ── single file row inside an entry ── */
const FileRow = ({
  entryIndex,
  fileIndex,
  fileName,
  isUploading,
  onFileChange,
  onFileRemove,
  docTypeLabel,
}) => {
  const [preview, setPreview] = useState(null);
  const uploadKey = `${entryIndex}-${fileIndex}`;
  const isThisUploading = isUploading === uploadKey;
  const hasFile = !!fileName;

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: fileIndex > 0 ? 6 : 0 }}
    >
      {/* Upload zone */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <input
          type="file"
          id={`doc-file-${entryIndex}-${fileIndex}`}
          accept=".jpg,.jpeg,.png,.doc,.docx,.pdf"
          disabled={isThisUploading}
          onChange={(e) => {
            if (e.target.files[0]) onFileChange(entryIndex, fileIndex, e.target.files[0]);
          }}
          aria-label={`Upload file ${fileIndex + 1}`}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            cursor: isThisUploading ? "not-allowed" : "pointer",
            width: "100%",
            height: "100%",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 8,
            border: hasFile ? "1.5px solid #10b981" : "1.5px dashed var(--glass-border)",
            background: hasFile ? "rgba(16,185,129,0.04)" : "var(--icon-btn-bg)",
            transition: "all 0.15s",
            pointerEvents: "none",
          }}
        >
          {isThisUploading ? (
            <span
              aria-hidden="true"
              style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                border: "2px solid rgba(99,102,241,0.3)",
                borderTopColor: "#6366f1",
                animation: "spin 0.7s linear infinite",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          ) : hasFile ? (
            <CheckCircle size={13} strokeWidth={2} style={{ color: "#10b981", flexShrink: 0 }} />
          ) : (
            <Upload
              size={13}
              strokeWidth={2}
              style={{ color: "var(--text-muted)", flexShrink: 0 }}
            />
          )}
          <span
            style={{
              fontSize: "0.8125rem",
              color: hasFile ? "#10b981" : "var(--text-muted)",
              fontWeight: hasFile ? 600 : 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {isThisUploading
              ? "Uploading…"
              : hasFile
                ? fileName.length > 30
                  ? fileName.slice(0, 27) + "…"
                  : fileName
                : "Choose file (JPG, PNG, PDF, DOC · max 1 MB)"}
          </span>
        </div>
      </div>

      {/* View button — opens preview modal */}
      {hasFile && (
        <>
          <button
            type="button"
            onClick={() => setPreview({ file: fileName, label: docTypeLabel })}
            aria-label="Preview uploaded file"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "7px 10px",
              borderRadius: 8,
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              color: "var(--brand-indigo)",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(99,102,241,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(99,102,241,0.08)";
            }}
          >
            <Eye size={12} strokeWidth={2} />
            View
          </button>
          {preview && (
            <DocPreviewModal
              file={preview.file}
              label={preview.label}
              onClose={() => setPreview(null)}
            />
          )}
        </>
      )}

      {/* Remove this file */}
      <button
        type="button"
        onClick={() => onFileRemove(entryIndex, fileIndex)}
        disabled={isThisUploading}
        aria-label={`Remove file ${fileIndex + 1}`}
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          flexShrink: 0,
          background: "rgba(244,63,94,0.08)",
          border: "1px solid rgba(244,63,94,0.2)",
          color: "#f43f5e",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isThisUploading ? "not-allowed" : "pointer",
          opacity: isThisUploading ? 0.5 : 1,
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          if (!isThisUploading) e.currentTarget.style.background = "rgba(244,63,94,0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(244,63,94,0.08)";
        }}
      >
        <X size={12} strokeWidth={2.5} />
      </button>
    </div>
  );
};

/* ── view mode file link with preview ── */
const ViewFileLink = ({ fileName, label, index, total }) => {
  const [preview, setPreview] = useState(null);
  return (
    <>
      <button
        type="button"
        onClick={() => setPreview({ file: fileName, label })}
        aria-label={`Preview ${label} file ${index + 1}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: "none",
          border: "none",
          padding: 0,
          color: "var(--brand-indigo)",
          fontSize: "0.8125rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <Eye size={12} strokeWidth={2} />
        {total > 1 ? `File ${index + 1}` : "View File"}
      </button>
      {preview && (
        <DocPreviewModal
          file={preview.file}
          label={preview.label}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

/* ── main component ── */
const DocumentUploadSection = ({
  documents = [],
  onAdd,
  onRemove,
  onTypeChange,
  onFileChange,
  onFileAdd,
  onFileRemove,
  uploadingIndex,
  errors = [],
  touched = [],
  viewMode = false,
  disabled = false,
}) => {
  const isReadOnly = viewMode || disabled;
  const docTypeOptions = useDocTypeOptions();

  /* ── compliance progress ── */
  const mandatoryCount = MANDATORY_DOCUMENT_TYPES.length;
  const uploadedMandatory = MANDATORY_DOCUMENT_TYPES.filter((m) => {
    const entry = documents.find((d) => d.document_type === m.value);
    return entry?.files?.some((f) => f?.file_name);
  }).length;
  const allMandatoryDone = uploadedMandatory === mandatoryCount;

  /* ── VIEW MODE ── */
  if (isReadOnly) {
    return (
      <div style={{ marginTop: 8 }}>
        <SectionDivider label={DOCUMENT_SECTION} />

        {/* Compliance summary in view mode */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 12,
            background: allMandatoryDone ? "rgba(16,185,129,0.06)" : "rgba(245,158,11,0.06)",
            border: `1px solid ${allMandatoryDone ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}`,
          }}
        >
          {allMandatoryDone ? (
            <CheckCircle size={14} strokeWidth={2} style={{ color: "#10b981", flexShrink: 0 }} />
          ) : (
            <AlertCircle size={14} strokeWidth={2} style={{ color: "#f59e0b", flexShrink: 0 }} />
          )}
          <span
            style={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: allMandatoryDone ? "#10b981" : "#f59e0b",
            }}
          >
            {uploadedMandatory}/{mandatoryCount} required documents uploaded
          </span>
          {!allMandatoryDone && (
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginLeft: "auto" }}>
              {MANDATORY_DOCUMENT_TYPES.filter(
                (m) =>
                  !documents
                    .find((d) => d.document_type === m.value)
                    ?.files?.some((f) => f?.file_name),
              )
                .map((m) => m.label)
                .join(", ")}{" "}
              missing
            </span>
          )}
        </div>

        {!documents || documents.length === 0 ? (
          <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>—</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {documents.map((entry, ei) => {
              const category = getDocTypeCategory(entry.document_type);
              const color = getCategoryColor(category);
              const files =
                entry.files || (entry.file_name ? [{ file_name: entry.file_name }] : []);
              const isMandatory = MANDATORY_DOC_VALUES.has(entry.document_type);
              const isUploaded = files.some((f) => f?.file_name);
              const isLast = ei === documents.length - 1;

              return (
                <div
                  key={ei}
                  style={{
                    padding: "12px 0",
                    borderBottom: isLast ? "none" : "1px solid var(--glass-border)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: files.filter((f) => f?.file_name).length > 0 ? 8 : 0,
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 9,
                        flexShrink: 0,
                        background: color + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isMandatory && isUploaded ? (
                        <CheckCircle size={13} strokeWidth={2} style={{ color: "#10b981" }} />
                      ) : (
                        <FileText size={13} strokeWidth={2} style={{ color }} />
                      )}
                    </div>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        color: "var(--text-primary)",
                      }}
                    >
                      {getDocTypeLabel(entry.document_type)}
                    </span>
                    {isMandatory && (
                      <span
                        style={{
                          padding: "1px 7px",
                          borderRadius: 999,
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          background: isUploaded ? "rgba(16,185,129,0.12)" : "rgba(244,63,94,0.12)",
                          color: isUploaded ? "#10b981" : "#f43f5e",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {isUploaded ? "✓ Required" : "Required"}
                      </span>
                    )}
                    {files.filter((f) => f?.file_name).length > 0 && (
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          background: color + "15",
                          color,
                          marginLeft: "auto",
                        }}
                      >
                        {files.filter((f) => f?.file_name).length} file
                        {files.filter((f) => f?.file_name).length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  <div
                    style={{ paddingLeft: 40, display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {files
                      .filter((f) => f?.file_name)
                      .map((f, fi) => (
                        <ViewFileLink
                          key={fi}
                          fileName={f.file_name}
                          label={getDocTypeLabel(entry.document_type)}
                          index={fi}
                          total={files.filter((x) => x?.file_name).length}
                        />
                      ))}
                    {!isUploaded && (
                      <span
                        style={{
                          fontSize: "0.8125rem",
                          color: "var(--text-muted)",
                          fontStyle: "italic",
                        }}
                      >
                        Not uploaded
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  /* ── EDIT MODE ── */
  const isAnyUploading = uploadingIndex !== null;
  const mandatoryEntries = documents.filter((d) => d.mandatory);
  const optionalEntries = documents.filter((d) => !d.mandatory);

  const renderEntryCard = (entry, ei, isMandatoryCard) => {
    const category = getDocTypeCategory(entry.document_type);
    const color = getCategoryColor(category);
    const files = entry.files || [];
    const uploadedFiles = files.filter((f) => f?.file_name);
    const hasTypeError = !!(errors[ei]?.document_type && touched[ei]?.document_type);
    const hasFilesError = !!(errors[ei]?.files && touched[ei]?.files);
    const isComplete = uploadedFiles.length > 0;

    return (
      <div
        key={`${isMandatoryCard ? "m" : "o"}-${ei}`}
        role="group"
        aria-label={
          isMandatoryCard
            ? `Required: ${getDocTypeLabel(entry.document_type)}`
            : `Document ${ei + 1}`
        }
        className="animate-fade-in"
        style={{
          borderRadius: 14,
          marginBottom: 10,
          background: "var(--glass-bg)",
          border: isMandatoryCard
            ? `1px solid ${isComplete ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.3)"}`
            : "1px solid var(--glass-border)",
          overflow: "hidden",
          transition: "box-shadow 0.15s",
        }}
      >
        {/* Accent bar */}
        <div
          style={{
            height: 3,
            background: isMandatoryCard
              ? isComplete
                ? "linear-gradient(90deg,#10b981,#34d399)"
                : "linear-gradient(90deg,#f59e0b,#fbbf24)"
              : `linear-gradient(90deg, ${color}, ${color}88)`,
          }}
        />

        <div style={{ padding: "14px 16px" }}>
          {/* Top row */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
            {/* Icon */}
            <div
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                flexShrink: 0,
                background: isMandatoryCard
                  ? isComplete
                    ? "rgba(16,185,129,0.12)"
                    : "rgba(245,158,11,0.12)"
                  : color + "15",
                border: `1px solid ${isMandatoryCard ? (isComplete ? "rgba(16,185,129,0.25)" : "rgba(245,158,11,0.25)") : color + "25"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 2,
              }}
            >
              {isMandatoryCard && isComplete ? (
                <CheckCircle size={15} strokeWidth={2} style={{ color: "#10b981" }} />
              ) : (
                <FileText
                  size={15}
                  strokeWidth={2}
                  style={{ color: isMandatoryCard ? (isComplete ? "#10b981" : "#f59e0b") : color }}
                />
              )}
            </div>

            {/* Label or dropdown */}
            <div style={{ flex: 1 }}>
              {isMandatoryCard ? (
                /* Mandatory: fixed label, no dropdown */
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "0.9375rem",
                        color: "var(--text-primary)",
                      }}
                    >
                      {getDocTypeLabel(entry.document_type)}
                    </span>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 999,
                        fontSize: "0.625rem",
                        fontWeight: 800,
                        background: isComplete ? "rgba(16,185,129,0.12)" : "rgba(244,63,94,0.12)",
                        color: isComplete ? "#10b981" : "#f43f5e",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                      }}
                    >
                      {isComplete ? "✓ Uploaded" : "Required"}
                    </span>
                  </div>
                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    {MANDATORY_DOCUMENT_TYPES.find((m) => m.value === entry.document_type)?.reason}
                  </span>
                </div>
              ) : (
                /* Optional: dropdown */
                <div>
                  <label
                    htmlFor={`doc-type-${ei}`}
                    style={{
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--text-muted)",
                      marginBottom: 4,
                      display: "block",
                    }}
                  >
                    Document Type
                  </label>
                  <div
                    id={`doc-type-${ei}`}
                    aria-describedby={hasTypeError ? `doc-type-err-${ei}` : undefined}
                  >
                    <BaseSelect
                      name={`document_type_${ei}`}
                      options={docTypeOptions}
                      placeholder="Select document type…"
                      value={entry.document_type || ""}
                      touched={hasTypeError}
                      error={hasTypeError ? errors[ei].document_type : undefined}
                      mb="mb-0"
                      handleChange={(_f, val) => onTypeChange(ei, val || "")}
                      handleBlur={() => {}}
                    />
                  </div>
                  {hasTypeError && (
                    <span
                      id={`doc-type-err-${ei}`}
                      role="alert"
                      style={{
                        fontSize: "0.75rem",
                        color: "#f43f5e",
                        marginTop: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <AlertCircle size={11} strokeWidth={2} />
                      {errors[ei].document_type}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Remove — only for optional entries */}
            {!isMandatoryCard && (
              <button
                type="button"
                onClick={() => onRemove(ei)}
                disabled={isAnyUploading}
                aria-label={`Remove document ${ei + 1}`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "rgba(244,63,94,0.08)",
                  border: "1px solid rgba(244,63,94,0.2)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isAnyUploading ? "not-allowed" : "pointer",
                  opacity: isAnyUploading ? 0.5 : 1,
                  transition: "all 0.15s",
                  marginTop: 2,
                }}
                onMouseEnter={(e) => {
                  if (!isAnyUploading) e.currentTarget.style.background = "rgba(244,63,94,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(244,63,94,0.08)";
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            )}
          </div>

          {/* Files section */}
          <div
            style={{
              paddingLeft: 44,
              borderTop: "1px solid var(--glass-border)",
              paddingTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--text-muted)",
                }}
              >
                Files
                {uploadedFiles.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      padding: "1px 7px",
                      borderRadius: 999,
                      background: "#10b98115",
                      color: "#10b981",
                      fontSize: "0.625rem",
                      fontWeight: 700,
                    }}
                  >
                    {uploadedFiles.length} uploaded
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => onFileAdd(ei)}
                disabled={isAnyUploading}
                aria-label={`Add another file to document ${ei + 1}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 7,
                  background:
                    (isMandatoryCard ? (isComplete ? "#10b981" : "#f59e0b") : color) + "10",
                  border: `1px solid ${isMandatoryCard ? (isComplete ? "#10b981" : "#f59e0b") : color}30`,
                  color: isMandatoryCard ? (isComplete ? "#10b981" : "#f59e0b") : color,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: isAnyUploading ? "not-allowed" : "pointer",
                  opacity: isAnyUploading ? 0.5 : 1,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isAnyUploading) e.currentTarget.style.opacity = "0.75";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = isAnyUploading ? "0.5" : "1";
                }}
              >
                <Plus size={11} strokeWidth={2.5} />
                Add File
              </button>
            </div>

            {files.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  borderRadius: 8,
                  textAlign: "center",
                  background: "var(--icon-btn-bg)",
                  border: "1.5px dashed var(--glass-border)",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                }}
              >
                No files yet — click &ldquo;Add File&rdquo; above
              </div>
            ) : (
              files.map((f, fi) => (
                <FileRow
                  key={fi}
                  entryIndex={ei}
                  fileIndex={fi}
                  fileName={f?.file_name || ""}
                  isUploading={uploadingIndex}
                  onFileChange={onFileChange}
                  onFileRemove={onFileRemove}
                  docTypeLabel={getDocTypeLabel(entry.document_type)}
                />
              ))
            )}

            {hasFilesError && (
              <span
                role="alert"
                style={{
                  fontSize: "0.75rem",
                  color: "#f43f5e",
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <AlertCircle size={11} strokeWidth={2} />
                {errors[ei].files}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 8 }}>
      <SectionDivider label={DOCUMENT_SECTION} />

      {/* ── Compliance progress bar ── */}
      <div
        style={{
          padding: "12px 16px",
          borderRadius: 12,
          marginBottom: 16,
          background: allMandatoryDone ? "rgba(16,185,129,0.06)" : "rgba(245,158,11,0.06)",
          border: `1px solid ${allMandatoryDone ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {allMandatoryDone ? (
              <CheckCircle size={14} strokeWidth={2} style={{ color: "#10b981" }} />
            ) : (
              <AlertCircle size={14} strokeWidth={2} style={{ color: "#f59e0b" }} />
            )}
            <span
              style={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: allMandatoryDone ? "#10b981" : "#f59e0b",
              }}
            >
              {allMandatoryDone
                ? "All required documents uploaded"
                : `${uploadedMandatory} of ${mandatoryCount} required documents uploaded`}
            </span>
          </div>
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: allMandatoryDone ? "#10b981" : "#f59e0b",
            }}
          >
            {Math.round((uploadedMandatory / mandatoryCount) * 100)}%
          </span>
        </div>
        {/* Progress bar */}
        <div
          style={{
            height: 5,
            borderRadius: 999,
            background: "var(--glass-border)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 999,
              width: `${(uploadedMandatory / mandatoryCount) * 100}%`,
              background: allMandatoryDone
                ? "linear-gradient(90deg,#10b981,#34d399)"
                : "linear-gradient(90deg,#f59e0b,#fbbf24)",
              transition: "width 0.4s ease",
            }}
          />
        </div>
        {!allMandatoryDone && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {MANDATORY_DOCUMENT_TYPES.map((m) => {
              const entry = documents.find((d) => d.document_type === m.value);
              const done = entry?.files?.some((f) => f?.file_name);
              return (
                <span
                  key={m.value}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    background: done ? "rgba(16,185,129,0.1)" : "rgba(244,63,94,0.08)",
                    color: done ? "#10b981" : "#f43f5e",
                    border: `1px solid ${done ? "rgba(16,185,129,0.2)" : "rgba(244,63,94,0.15)"}`,
                  }}
                >
                  {done ? "✓" : "○"} {m.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Required documents section ── */}
      <div style={{ marginBottom: 4 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontSize: "0.6875rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "#f43f5e",
              padding: "2px 8px",
              borderRadius: 6,
              background: "rgba(244,63,94,0.08)",
              border: "1px solid rgba(244,63,94,0.15)",
            }}
          >
            Required
          </span>
          <div style={{ flex: 1, height: 1, background: "var(--glass-border)" }} />
        </div>
        {mandatoryEntries.map((entry) => {
          const ei = documents.indexOf(entry);
          return renderEntryCard(entry, ei, true);
        })}
      </div>

      {/* ── Optional documents section ── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "var(--text-muted)",
                padding: "2px 8px",
                borderRadius: 6,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              Optional
            </span>
            <div style={{ height: 1, width: 40, background: "var(--glass-border)" }} />
          </div>
          <button
            type="button"
            onClick={onAdd}
            disabled={isAnyUploading}
            aria-label="Add optional document"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 9,
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              color: "var(--brand-indigo)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: isAnyUploading ? "not-allowed" : "pointer",
              opacity: isAnyUploading ? 0.6 : 1,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!isAnyUploading) e.currentTarget.style.background = "rgba(99,102,241,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(99,102,241,0.08)";
            }}
          >
            <Plus size={13} strokeWidth={2.5} />
            Add Document
          </button>
        </div>

        {optionalEntries.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px",
              gap: 6,
              background: "var(--icon-btn-bg)",
              borderRadius: 10,
              border: "1.5px dashed var(--glass-border)",
            }}
          >
            <FileText
              size={22}
              strokeWidth={1.5}
              style={{ color: "var(--text-muted)", opacity: 0.4 }}
            />
            <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
              No optional documents added
            </span>
          </div>
        ) : (
          optionalEntries.map((entry) => {
            const ei = documents.indexOf(entry);
            return renderEntryCard(entry, ei, false);
          })
        )}
      </div>
    </div>
  );
};

export default DocumentUploadSection;
